import React, { useState, useEffect } from 'react';
import { Card, Col, Row, Button, Modal, Form, Spinner, Nav, Image, InputGroup, FormControl } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useAuth } from 'context/AuthWizardContext';
import './MembersList.css';
import { FaUserCircle } from 'react-icons/fa';

const AdminPanel = () => {
  const [members, setMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]); // For filtered members based on search
  const [searchTerm, setSearchTerm] = useState(''); // Search term state
  const [selectedMember, setSelectedMember] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('members'); // Default tab to "members"
  const { user } = useAuth();
  const [customers, setCustomers] = useState([]); // New state for customers

  useEffect(() => {
    if (activeTab === 'members') {
      fetchMembers();
      fetchCustomers();
    }
  }, [activeTab]);

  useEffect(() => {
    if (searchTerm) {
      const filtered = members.filter(member =>
        member.uname.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredMembers(filtered);
    } else {
      setFilteredMembers(members);
    }
  }, [searchTerm, members]);

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const response = await axios.post('https://musteri.guardnet.com.tr/call.php', {
        token: user.token,
        process: "100019329"
      });
      if (response.data && response.data.members) {
        setMembers(response.data.members);
        setFilteredMembers(response.data.members);
      } else {
        setMembers([]);
        setFilteredMembers([]);
      }
    } catch (error) {
      console.error('Error fetching members:', error);
      setError('Üyeleri yüklerken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.post('https://musteri.guardnet.com.tr/call.php', {
        token: user.token,
        process: "100019332"
      });
      if (response.data && response.data.customers) {
        setCustomers(response.data.customers);
      } else {
        setCustomers([]);
      }
    } catch (error) {
      setError('Veriler alınırken bir hata oluştu.');
      console.error('Error fetching customers:', error);
    }
  };

  const handleSelectMember = (member) => {
    setSelectedMember(member);
    setFormData(member);  // Set form data with selected member details
    setShowModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
  
    const requestData = new FormData();
    requestData.append('process', '100019331');
    requestData.append('uname', formData.uname);
    requestData.append('email', formData.email);
    requestData.append('phone', formData.PHONE);
    requestData.append('intro', formData.intro);
    requestData.append('title', formData.TITLE);
    requestData.append('customer_id', formData.customer_id); 
    requestData.append('token', user.token);

    try {
      const response = await axios.post('https://musteri.guardnet.com.tr/call.php', requestData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      if (response.data.success) {
        toast.success('Profil başarıyla güncellendi!');
        setShowModal(false);
        fetchMembers(); // Refresh members list after update
      } else {
        toast.error('Güncelleme başarısız: ' + response.data.message);
      }
    } catch (error) {
      toast.error('Bir hata oluştu: ' + error.message);
    }
  };

  return (
    <Row className="gx-3">
      <Col md={3}>
        <Nav variant="pills" className="flex-column" activeKey={activeTab} onSelect={(selectedKey) => setActiveTab(selectedKey)}>
          <Nav.Item>
            <Nav.Link eventKey="members">Member Management</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="customers">Customer Management</Nav.Link>
          </Nav.Item>
        </Nav>
      </Col>

      <Col md={9} className="members-list-container">
        {activeTab === 'members' && (
          <>
            <h4 className="section-title">Members</h4>
            {/* Search Bar */}
            <InputGroup className="mb-4">
              <FormControl
                placeholder="Search members..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
            <Row className="gy-4">
              {loading ? (
                <div className="text-center">
                  <Spinner animation="border" />
                </div>
              ) : error ? (
                <p className="text-danger">{error}</p>
              ) : filteredMembers.length > 0 ? (
                filteredMembers.map((member) => (
                  <Col xs={6} md={4} lg={3} key={member.memid}>
                    <Card className="member-card small-card">
                    <Card.Body className="text-center">
                        {member.image ? (
                          <Image
                            src={member.image}
                            roundedCircle
                            className="member-image small-image"
                          />
                        ) : (
                        <FaUserCircle size={50} />
                        )}
                        <h6 className="mt-2">{member.uname}</h6>
                        <p className="text-muted small-title">{member.TITLE || 'No title'}</p>
                        <Button
                          variant="primary"
                          onClick={() => handleSelectMember(member)}
                          className="w-100 btn-sm"
                        >
                          Edit Member
                        </Button>
                      </Card.Body>
                    </Card>
                  </Col>
                ))
              ) : (
                <p className="text-center">Üye bulunamadı</p>
              )}
            </Row>
          </>
        )}
        {activeTab === 'customers' && (
          <div className="customer-management">
            <h4 className="section-title">Customer Management</h4>
            <p className="text-center">Customer management content will go here.</p>
          </div>
        )}
      </Col>

      {/* Modal for editing member */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Üye Bilgilerini Düzenle</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3" controlId="uname">
              <Form.Label>İsim</Form.Label>
              <Form.Control
                type="text"
                name="uname"
                value={formData.uname || ''}
                onChange={handleInputChange}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="email">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={formData.email || ''}
                onChange={handleInputChange}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="phone">
              <Form.Label>Telefon</Form.Label>
              <Form.Control
                type="text"
                name="PHONE"
                value={formData.PHONE || ''}
                onChange={handleInputChange}
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="title">
              <Form.Label>Başlık</Form.Label>
              <Form.Control
                type="text"
                name="TITLE"
                value={formData.TITLE || ''}
                onChange={handleInputChange}
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="customer_id">
              <Form.Label>Müşteri ID</Form.Label>
              <Form.Control
                as="select"
                name="customer_id"
                value={formData.customer_id || ''}
                onChange={handleInputChange}
              >
                <option value="">Müşteri Seçin</option>
                {customers.map((customer) => (
                  <option key={customer.id} value={customer.id}>
                    {customer.name}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>

            <Form.Group className="mb-3" controlId="intro">
              <Form.Label>Tanıtım</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="intro"
                value={formData.intro || ''}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Button variant="primary" type="submit">
              Güncelle
            </Button>
          </Form>
        </Modal.Body>
      </Modal>
    </Row>
  );
};

export default AdminPanel;
